'use client';
import clsx from 'clsx';
import type React from 'react';
import { Children, type ReactElement, useState } from 'react';

import Chip from './chip';

interface Question {
  Key: string;
  Text: string;
  Options: string[];
}

export interface ResultProps {
  RequiredAnswers: string[] | { [key: string]: string | string[] };
  default?: boolean;
  children: React.ReactNode;
}

export interface QuizProps {
  Questions: Question[];
  children: React.ReactNode;
}

export const Result = (_props: ResultProps) => {
  // This component is just a placeholder as the logic will be in the Quiz component
  return null;
};

function Quiz(props: QuizProps) {
  const { Questions, children } = props;

  // Function to generate a key from question text
  const generateKeyFromText = (text: string) => {
    return text.replace('?', '').toLowerCase().replace(/\s+/g, '-');
  };

  // Transform Questions to ensure each has a key
  const transformedQuestions = Questions.map((question) => ({
    ...question,
    Key: question.Key && question.Key !== '' ? question.Key : generateKeyFromText(question.Text),
  }));

  const getResultsFromChildren = () => {
    let defaultResult = null;
    const specificResults = Children.toArray(children)
      .filter((child): child is ReactElement<ResultProps> => {
        return (child as ReactElement<ResultProps>).type === Result;
      })
      .map((child: ReactElement<ResultProps>) => {
        if (child.props.default) {
          defaultResult = child.props.children;
          return null;
        }
        const requiredAnswers = child.props.RequiredAnswers;
        let answerObject = requiredAnswers;
        if (Array.isArray(requiredAnswers)) {
          answerObject = requiredAnswers.reduce(
            (acc, answer, index) => {
              const questionKey = transformedQuestions[index]?.Key || '';
              acc[questionKey] = answer;
              return acc;
            },
            {} as { [key: string]: string },
          );
        }
        return {
          RequiredAnswers: answerObject,
          ResultText: child.props.children,
        };
      })
      .filter(Boolean);

    return { specificResults, defaultResult };
  };

  const { specificResults, defaultResult } = getResultsFromChildren();

  // Initialize answers as an object with keys from transformedQuestions
  const initialAnswers = transformedQuestions.reduce(
    (acc, question) => {
      acc[question.Key || ''] = '';
      return acc;
    },
    {} as { [key: string]: string },
  );
  const [answers, setAnswers] = useState<{ [key: string]: string }>(initialAnswers);
  const [finalResult, setFinalResult] = useState<React.ReactNode>();

  const checkForResult = (newAnswers: { [key: string]: string }): React.ReactNode => {
    for (const result of specificResults) {
      const isMatch = Object.entries(
        result?.RequiredAnswers as { [key: string]: string | string[] },
      ).every(([key, value]) => {
        if (Array.isArray(value)) {
          // If value is an array, check if the answer matches any of the values in the array
          // Or if the array includes an empty string and the answer is also an empty string
          return value.includes(newAnswers[key]) || (value.includes('') && newAnswers[key] === '');
        }
        // If value is a string, it must match the answer exactly
        return value === newAnswers[key];
      });

      if (isMatch) return result?.ResultText;
    }
    return Object.values(newAnswers).some((answer) => answer !== '') ? defaultResult : '';
  };

  const handleOptionClick = (questionKey: string, option: string) => {
    const newAnswers = {
      ...answers,
      [questionKey]: answers[questionKey] === option ? '' : option,
    };
    setAnswers(newAnswers);

    const result = checkForResult(newAnswers);
    setFinalResult(result);
  };

  const renderQuestions = () => {
    return transformedQuestions.map((item) => (
      <div key={item.Key}>
        <div className="flex items-center align-center gap-1 text-md font-medium">
          <p>{item.Text} </p> {answers[item.Key] && <Chip color="green" label="" icon={'Check'} />}
        </div>
        <div className="flex gap-2">
          {item.Options.map((option) => (
            <button
              key={option}
              type="button"
              className={clsx(
                'rounded-lg px-2 border-solid border dark:border-neutral-800 hover:bg-neutral-200 dark:hover:bg-neutral-700',
                answers[item.Key] === option && 'bg-neutral-200 dark:bg-neutral-700',
              )}
              onClick={() => handleOptionClick(item.Key, option)}
            >
              {option}
            </button>
          ))}
        </div>
      </div>
    ));
  };

  return (
    <div>
      {renderQuestions()}
      {finalResult && (
        <div className="bg-neutral-100 dark:bg-neutral-900 rounded-lg px-2 pb-1 my-4">
          <span className="text-xs uppercase font-semibold tracking-widest opacity-30">Result</span>
          <div className="px-1 my-0">{finalResult}</div>
        </div>
      )}
    </div>
  );
}

export default Quiz;
